import { useGetCurrentUser } from '@/hooks/api/users/useGetCurrentUser';
import { useIndexPage } from '@/hooks/useIndexPage';
import { usePageViewRecording } from '@/hooks/usePageViewRecording';
import { usePopover } from '@/hooks/usePopover';
import { useTrialStatus } from '@/hooks/useTrialStatus';
import { TenantUrlSync } from '@/services/tenants/TenantUrlSync';
import { useTenants } from '@/services/tenants/useTenants';
import { PhysnaLogo } from '@/svg/PhysnaLogo';
import { Settings } from '@mui/icons-material';
import { Box, Divider, IconButton, Link as MuiLink } from '@mui/material';
import { styled } from '@mui/material/styles';
import React from 'react';
import { Link } from 'react-router-dom';
import { StyledList, StyledListHeader, StyledListItem } from '../AssetsPage/PopoverList';
import { BodyText } from '../BodyText';
import { RoundedPopover, popoverRightProperties } from '../Popover/RoundedPopover';
import { TrialGuard } from '../TrialGuard';
import { AccountButton } from './AccountButton';
import { HelpButton } from './HelpButton';
import { Navigation } from './Navigation';
import { TenantSelector } from './TenantSelector';
import { TrialStatus } from './TrialStatus';
import { HelpContent } from '../HelpContent/HelpContent';
import { ThemeModeSelector } from './ThemeModeSelector';

const PageContainer = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  height: '100vh',
  backgroundColor: '#000000',
  color: '#ffffff',
});

const Header = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '16px',
  backgroundColor: '#000000',
  height: '62px',
});

const LogoLink = styled(Link)({
  display: 'flex',
  alignItems: 'center',
  cursor: 'pointer',
  textDecoration: 'none',
});

const HeaderIcons = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
});

const ContentWrapper = styled(Box)({
  display: 'flex',
  flex: 1,
  overflow: 'hidden',
  margin: '0 8px 8px 8px',
  gap: '10px',
  minHeight: 0,
});

const MainContent = styled(Box)(({ theme }) => ({
  display: 'flex',
  flex: 1,
  overflow: 'auto',
  backgroundColor: theme.palette.background.default,
  borderRadius: '8px',
  minWidth: 0,
}));

const StyledIconButton = styled(IconButton)({
  color: '#ffffff',
  '&:hover': {
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
  },
});

const StyledLink = styled(Link)({
  textDecoration: 'none',
  color: 'inherit',
});

export const Layout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { getPath } = useTenants();
  const { permissions, isLoading, currentUserIsAtleastTenantViewer, currentTenant } = useGetCurrentUser();
  const { indexPagePath } = useIndexPage();
  const { open, close, isOpen, anchorElement } = usePopover();
  const trialStatus = useTrialStatus();

  usePageViewRecording();

  return (
    <PageContainer>
      <TenantUrlSync />
      <Header>
        <Box display='flex' alignItems='center' gap='16px' flex='1' minWidth={0}>
          <LogoLink to={indexPagePath}>
            <PhysnaLogo size={3.5} textColor='#ffffff' dotColor='#387755' title='Physna' />
          </LogoLink>
          <TenantSelector />
          <TrialStatus />
        </Box>
        <HeaderIcons>
          <AccountButton />
          <StyledIconButton onClick={open}>
            <Settings />
          </StyledIconButton>
          <HelpButton />
        </HeaderIcons>
        <RoundedPopover open={isOpen} anchorEl={anchorElement} onClose={close} {...popoverRightProperties}>
          <StyledList sx={{ minWidth: '160px' }}>
            <StyledListHeader>
              <BodyText>System</BodyText>
            </StyledListHeader>
            <Divider />
            {currentUserIsAtleastTenantViewer(currentTenant?.tenantId) && (
              <StyledLink to={getPath('status')} style={{ textDecoration: 'none', color: 'inherit' }} onClick={close}>
                <StyledListItem>
                  <BodyText>Status</BodyText>
                </StyledListItem>
              </StyledLink>
            )}
            {permissions.canManageUsers && (
              <StyledLink to={getPath('users')} style={{ textDecoration: 'none', color: 'inherit' }} onClick={close}>
                <StyledListItem>
                  <BodyText>Users</BodyText>
                </StyledListItem>
              </StyledLink>
            )}
            {permissions.canManageMetadataFields && (
              <StyledLink to={getPath('metadata')} style={{ textDecoration: 'none', color: 'inherit' }} onClick={close}>
                <StyledListItem>
                  <BodyText>Metadata Fields</BodyText>
                </StyledListItem>
              </StyledLink>
            )}
            {permissions.canAddMetadataFields && permissions.canModifyAssetMetadata && (
              <StyledLink
                to={getPath('metadata-upload')}
                style={{ textDecoration: 'none', color: 'inherit' }}
                onClick={close}
              >
                <StyledListItem>
                  <BodyText>Metadata Upload</BodyText>
                </StyledListItem>
              </StyledLink>
            )}
            <StyledLink
              to='/changelog'
              style={{
                textDecoration: 'none',
              }}
              onClick={close}
            >
              <StyledListItem>
                <BodyText>Changelog</BodyText>
              </StyledListItem>
            </StyledLink>
            <MuiLink
              href={`mailto:${import.meta.env.VITE_SUPPORT_EMAIL || 'support@physna.com'}`}
              target='_blank'
              style={{
                textDecoration: 'none',
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'space-between',
              }}
              onClick={close}
            >
              <StyledListItem>
                <BodyText>Email Support</BodyText>
              </StyledListItem>
            </MuiLink>
            <MuiLink
              href='/privacy-policy.pdf'
              target='_blank'
              style={{
                textDecoration: 'none',
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'space-between',
              }}
              onClick={close}
            >
              <StyledListItem>
                <BodyText>Privacy Policy</BodyText>
              </StyledListItem>
            </MuiLink>
            <Divider />
            <ThemeModeSelector />
          </StyledList>
        </RoundedPopover>
      </Header>
      <ContentWrapper>
        <MainContent>
          {!isLoading && !permissions.canOnlySearch && !trialStatus.isExpired && <Navigation />}
          {!isLoading && <TrialGuard>{children}</TrialGuard>}
        </MainContent>
        <HelpContent />
      </ContentWrapper>
    </PageContainer>
  );
};
